import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { startVersionCheck, stopVersionCheck } from './utils/versionChecker';

// Editor Components
import { EditorProvider } from './contexts/EditorContext';
import EditorToolbar from './components/editor/EditorToolbar';
import EditorPanel from './components/editor/EditorPanel';

// Layout Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ThemeProvider from './components/ThemeProvider';
import AdminLayout from './components/AdminLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import MagicLogin from './pages/MagicLogin';
import Profile from './pages/Profile';
import Products from './pages/Products';
import SalePage from './pages/SalePage';
import ProductDetail from './pages/ProductDetail';
import BrandPage from './pages/BrandPage';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';
import Wishlist from './pages/Wishlist';
import Notepad from './pages/Notepad';
import NewsletterSignup from './pages/NewsletterSignup';
import NewsletterConfirm from './pages/NewsletterConfirm';
import NotFound from './pages/NotFound';
import CheckoutSuccess from './pages/CheckoutSuccess';
import BankTransferSuccess from './pages/BankTransferSuccess';
import OrderSuccess from './pages/OrderSuccess';
import Impressum from './pages/Impressum';
import AGB from './pages/AGB';
import Widerruf from './pages/Widerruf';
import CookieBanner from './components/CookieBanner';
import CartReminderBanner from './components/CartReminderBanner';
import GoogleTracking from './components/GoogleTracking';
import CopyProtection from './components/CopyProtection';
import SeasonalDecorations from './components/SeasonalDecorations';
import ThemeBackgrounds from './components/ThemeBackgrounds';
import MobileBottomNav from './components/MobileBottomNav';
import SupportChatWidget from './components/SupportChatWidget';
import DesktopSidebar from './components/DesktopSidebar';
import SearchPage from './pages/SearchPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogTopicPage from './pages/BlogTopicPage';
import CategoriesPage from './pages/CategoriesPage';
import AccountPage from './pages/AccountPage';
import AdminLogin from './pages/AdminLogin';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import OrdersManager from './pages/admin/OrdersManager';
import AdminProducts from './pages/admin/Products';
import AdminNewsletter from './pages/admin/Newsletter';
import NewsletterSettings from './pages/admin/NewsletterSettings';
import DesignManager from './pages/admin/DesignManager';
import Categories from './pages/admin/Categories';
import DiscountCodes from './pages/admin/DiscountCodes';
import PaymentSettings from './pages/admin/PaymentSettings';
import PartnerSites from './pages/admin/PartnerSites';
import LegalPages from './pages/admin/LegalPages';
import EmailSettings from './pages/admin/EmailSettings';
import EmailTemplatePreview from './pages/admin/EmailTemplatePreview';
import CollectionDetail, { CollectionsOverview } from './pages/CollectionDetail';
import HairCollectionsManager from './pages/admin/HairCollectionsManager';
import AbandonedCartsManager from './pages/admin/AbandonedCartsManager';
import Invoices from './pages/admin/Invoices';
import BrandsManager from './pages/admin/BrandsManager';
import GoogleIntegration from './pages/admin/GoogleIntegration';
import DocumentSettings from './pages/admin/DocumentSettings';
import AdminSettings from './pages/admin/Settings';
import ShippingSettings from './pages/admin/ShippingSettings';
import AppIconSettings from './pages/admin/AppIconSettings';
import NewsletterVisualEditor from './pages/admin/NewsletterVisualEditor';
import VisualDesignEditor from './pages/admin/VisualDesignEditorNew';
import VisualPageEditor from './pages/admin/VisualPageEditor';
import SeasonalThemes from './pages/admin/SeasonalThemesNew';
import SeasonalThemeEditor from './pages/admin/SeasonalThemeEditor';
import MyCoupons from './pages/MyCoupons';
import CatalogsManager from './pages/admin/CatalogsManager';
import PromotionSettings from './pages/admin/PromotionSettings';
import SocialMediaSettings from './pages/admin/SocialMediaSettings';
import MyOrders from './pages/MyOrders';
import MyInvoices from './pages/MyInvoices';
import MyAccount from './pages/MyAccount';
import Customers from './pages/admin/Customers';
import SeasonalLandingPages from './pages/admin/SeasonalLandingPages';
import SeasonalLandingPage from './pages/SeasonalLandingPage';
import MessageCenter from './pages/admin/MessageCenter';
import Contact from './pages/Contact';
import Faq from './pages/Faq';
import PushNotifications from './pages/admin/PushNotifications';
import HomepageManager from './pages/admin/HomepageManager';
import SeasonalSearchManager from './pages/admin/SeasonalSearchManager';
import BlogManager from './pages/admin/BlogManager';
import FaqManager from './pages/admin/FaqManager';
import ProductImageQualityReport from './pages/admin/ProductImageQualityReport';
import ContentCreator from './pages/admin/ContentCreator';
import ShippingZones from './pages/admin/ShippingZones';
import BlogTopicsManager from './pages/admin/BlogTopicsManager';
import BlogQuestionsManager from './pages/admin/BlogQuestionsManager';
import SectionTemplates from './pages/admin/SectionTemplates';
import DocumentsManager from './pages/admin/DocumentsManager';
import VendorsManager from './pages/admin/VendorsManager';
import VendorSettings from './pages/admin/VendorSettings';
import CommissionTracking from './pages/admin/CommissionTracking';
import SupportCenter from './pages/admin/SupportCenter';
import AdminManagement from './pages/admin/AdminManagement';
import AdminProfile from './pages/admin/AdminProfile';
import AdminInbox from './pages/admin/AdminInbox';
import ContentStudio from './pages/admin/ContentStudio';
import CatalogPDF from './pages/admin/CatalogPDF';
import NewsletterBuilder from './pages/admin/NewsletterBuilder';
import NewsletterDesigner from './pages/admin/NewsletterDesigner';
import Inventory from './pages/admin/Inventory';
import ProductDraftsManager from './pages/admin/ProductDraftsManager';
import LabelComposer from './pages/admin/LabelComposer';
import ReturnsManager from './pages/admin/ReturnsManager';
import ReturnsPage from './pages/ReturnsPage';
import AdminManual from './pages/admin/AdminManual';
import FeedbackManager from './pages/admin/FeedbackManager';
import CampaignManager from './pages/admin/CampaignManager';
import BenefitsManager from './pages/admin/BenefitsManager';
import VendorFeaturesManager from './pages/admin/VendorFeaturesManager';
import AdminPreview from './pages/admin/AdminPreview';
import MarketplaceSettings from './pages/admin/MarketplaceSettings';
import MaintenanceSettings from './pages/admin/MaintenanceSettings';
import LaunchChecklist from './pages/admin/LaunchChecklist';
import TestEmail from './pages/admin/TestEmail';
import ProductCatalogGenerator from './pages/admin/ProductCatalogGenerator';
import VendorRegister from './pages/VendorRegister';
import VendorDashboard from './pages/VendorDashboard';
import VendorPaymentSettings from './pages/VendorPaymentSettings';
import VendorShop from './pages/VendorShop';
import VendorProductEditor from './pages/VendorProductEditor';
import VendorShopCustomization from './pages/VendorShopCustomization';
import VendorShopTemplates from './pages/VendorShopTemplates';
import BecomeVendor from './pages/BecomeVendor';
import ProductImport from './pages/ProductImport';
import ImpersonateLogin from './pages/ImpersonateLogin';
import MaintenanceMode from './components/MaintenanceMode';
import useAuthStore from './store/authStore';
import { ErrorBoundary } from './components/ErrorBoundary';

// Maintenance Mode Wrapper
function MaintenanceWrapper({ children }) {
  // Prime the state from sessionStorage so we don't flash the shop on
  // page reload when maintenance mode is already active.
  const cached = (() => {
    try {
      const raw = sessionStorage.getItem('maintenanceSettings');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [maintenanceSettings, setMaintenanceSettings] = React.useState(cached);
  const [loading, setLoading] = React.useState(cached === null);
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  React.useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/maintenance`);
        if (response.ok) {
          const data = await response.json();
          setMaintenanceSettings(data);
          try {
            sessionStorage.setItem('maintenanceSettings', JSON.stringify(data));
          } catch {
            // sessionStorage may be unavailable (private mode) — ignore
          }
        }
      } catch (error) {
        console.log('Could not fetch maintenance status');
      } finally {
        setLoading(false);
      }
    };
    fetchMaintenanceStatus();
  }, []);

  // Determine access synchronously based on current state
  const isMaintenanceMode = maintenanceSettings?.maintenance_mode;
  const isAdmin = isAuthenticated && user?.role === 'admin';
  const { pathname } = useLocation();
  const isAdminPath = pathname.startsWith('/admin') || pathname === '/login';
  const legalPaths = ['/impressum', '/agb', '/datenschutz', '/widerruf', '/versand'];
  const isLegalPath = legalPaths.some(p => pathname.startsWith(p));

  // If maintenance is on and the visitor is not an admin / not on an
  // allowed path, render ONLY the maintenance page (no banners, no
  // bottom-nav — they are children here, so they are correctly hidden).
  const showMaintenance = isMaintenanceMode && !isAdmin && !isAdminPath && !isLegalPath;
  React.useEffect(() => {
    document.body.classList.toggle('maintenance-active', !!showMaintenance);
  }, [showMaintenance]);

  if (showMaintenance) {
    return (
      <div className="App flex flex-col min-h-screen" data-testid="maintenance-layout">
        <Navbar maintenance />
        <main className="flex-1 flex">
          <MaintenanceMode settings={maintenanceSettings} embedded />
        </main>
      </div>
    );
  }

  // Initial load without cache: don't flash the shop to visitors who
  // might get redirected to the maintenance page. Render a tiny spinner.
  if (loading) {
    return (
      <div
        data-testid="maintenance-check-loader"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F5EDE4',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid #D4C5B0',
            borderTopColor: '#8B7355',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return children;
}

// Component to handle navigation state and scroll behavior
function NavigationHandler() {
  const location = useLocation();

  // Swap PWA manifest depending on URL path:
  //  - on /admin/*  → admin-manifest.json (installs as "Homeyoursoul Admin"
  //    with start_url=/admin-login so the installed app opens the admin area)
  //  - everywhere else → manifest.json (customer shop PWA)
  // Without this, installing from the admin area uses the customer manifest
  // and the app icon always opens the shop homepage.
  useEffect(() => {
    const isAdminPath = location.pathname.startsWith('/admin') || location.pathname === '/admin-login';
    const desired = isAdminPath ? '/admin-manifest.json' : '/manifest.json';
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== desired) {
      link.setAttribute('href', desired);
    }
    // Also adapt the theme-color so the OS status bar matches the section
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.setAttribute('content', isAdminPath ? '#5D4E3C' : '#C4A084');
    }
    // Toggle a body class so global CSS can offset the customer pages by the
    // width of the persistent DesktopSidebar (220px) on lg+ viewports.
    if (isAdminPath || location.pathname === '/login' || location.pathname === '/register') {
      document.body.classList.remove('has-desktop-sidebar');
    } else {
      document.body.classList.add('has-desktop-sidebar');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    
    // Mark navigation as complete to prevent hanging
    const timer = setTimeout(() => {
      if (document.readyState === 'complete') {
        window.dispatchEvent(new Event('locationchange'));
      }
    }, 0);
    
    // Cleanup to prevent memory leaks
    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname]); // Only trigger on pathname change
  
  return null;
}

function App() {
  // Start automatic version checking
  useEffect(() => {
    startVersionCheck();
    return () => stopVersionCheck();
  }, []);

  // Wrapper component that hides the chat widget on admin / vendor pages
  // (#8 from user backlog: the inquiry chat is for customers / vendors only).
  function CustomerOnlyChatWidget() {
    const { pathname } = useLocation();
    if (pathname.startsWith('/admin')) return null;
    return <SupportChatWidget />;
  }

  // ViewportLanding — renders the welcome/home page on desktop (lg+, ≥1024px)
  // and the shop directly on mobile/tablet. This gives the desktop visitor a
  // curated entry (hero, story, sections) while keeping the fast mobile UX of
  // landing straight on products.
  function ViewportLanding() {
    const [isDesktop, setIsDesktop] = useState(
      typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false
    );
    useEffect(() => {
      const mq = window.matchMedia('(min-width: 1024px)');
      const handler = (e) => setIsDesktop(e.matches);
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else mq.addListener(handler);
      return () => {
        if (mq.removeEventListener) mq.removeEventListener('change', handler);
        else mq.removeListener(handler);
      };
    }, []);
    return isDesktop ? <Home /> : <Products />;
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ThemeProvider>
        <EditorProvider>
          <EditorToolbar />
          <EditorPanel />
          <NavigationHandler />
          <GoogleTracking />
          <MaintenanceWrapper>
          <CookieBanner />
          <CartReminderBanner />
          <CopyProtection />
          <SeasonalDecorations />
          <MobileBottomNav />
          <DesktopSidebar />
          <CustomerOnlyChatWidget />
          <Routes>
          {/* Customer Routes */}
          {/* Landing behaviour:
              - Desktop (lg+, ≥1024px): show the Home / welcome page (better visual entry)
              - Mobile/Tablet: show the Shop directly (faster path to product browsing) */}
          <Route path="/" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1">
                <ViewportLanding />
              </main>
              <Footer />
            </div>
          } />
          <Route path="/home" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Home /></main>
              <Footer />
            </div>
          } />
          <Route path="/login" element={<Login />} />
          <Route path="/collections" element={<CollectionsOverview />} />
          <Route path="/collections/:slug" element={<CollectionDetail />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          {/* Common typos / mis-bookmarked variants — always redirect to the real login */}
          <Route path="/admin/login" element={<Navigate to="/admin-login.html" replace />} />
          <Route path="/adminlogin" element={<Navigate to="/admin-login.html" replace />} />
          <Route path="/login-admin" element={<Navigate to="/admin-login.html" replace />} />
          <Route path="/impersonate" element={<ImpersonateLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/magic-login" element={<MagicLogin />} />
          <Route path="/search" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><SearchPage /></main>
              <Footer />
            </div>
          } />
          <Route path="/profile" element={<Profile />} />
          <Route path="/register" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Register /></main>
              <Footer />
            </div>
          } />
          <Route path="/products" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Products /></main>
              <Footer />
            </div>
          } />
          {/* /shop alias — covers old bookmarks / external links */}
          <Route path="/shop" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Products /></main>
              <Footer />
            </div>
          } />
          <Route path="/sale" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><SalePage /></main>
              <Footer />
            </div>
          } />
          <Route path="/products/:id" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><ProductDetail /></main>
              <Footer />
            </div>
          } />
          <Route path="/product/:id" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><ProductDetail /></main>
              <Footer />
            </div>
          } />
          <Route path="/marke/:slug" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><BrandPage /></main>
              <Footer />
            </div>
          } />
          <Route path="/cart" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Cart /></main>
              <Footer />
            </div>
          } />
          <Route path="/checkout" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Checkout /></main>
              <Footer />
            </div>
          } />
          <Route path="/orders" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Orders /></main>
              <Footer />
            </div>
          } />
          <Route path="/wishlist" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Wishlist /></main>
              <Footer />
            </div>
          } />
          <Route path="/notepad" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Notepad /></main>
              <Footer />
            </div>
          } />
          <Route path="/checkout/success" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><CheckoutSuccess /></main>
              <Footer />
            </div>
          } />
          <Route path="/checkout/bank-transfer-success" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><BankTransferSuccess /></main>
              <Footer />
            </div>
          } />
          <Route path="/order-success" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><OrderSuccess /></main>
              <Footer />
            </div>
          } />
          <Route path="/impressum" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Impressum /></main>
              <Footer />
            </div>
          } />
          <Route path="/datenschutz" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><AGB slug="datenschutz" /></main>
              <Footer />
            </div>
          } />
          <Route path="/agb" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><AGB /></main>
              <Footer />
            </div>
          } />
          <Route path="/versand" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><AGB slug="versand" /></main>
              <Footer />
            </div>
          } />
          <Route path="/widerruf" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Widerruf /></main>
              <Footer />
            </div>
          } />
          <Route path="/newsletter" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><NewsletterSignup /></main>
              <Footer />
            </div>
          } />
          <Route path="/newsletter/confirm/:token" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><NewsletterConfirm /></main>
              <Footer />
            </div>
          } />
          <Route path="/blog" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><Blog /></main>
              <Footer />
            </div>
          } />
          <Route path="/blog/themen/:slug" element={
            <div className="App flex flex-col min-h-screen">
              <main className="flex-1"><BlogTopicPage /></main>
              <Footer />
            </div>
          } />
          <Route path="/blog/:id" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><BlogPost /></main>
              <Footer />
            </div>
          } />
          <Route path="/categories" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><CategoriesPage /></main>
              <Footer />
            </div>
          } />
          <Route path="/my-coupons" element={<MyCoupons />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/my-invoices" element={<MyInvoices />} />
          <Route path="/my-account" element={<MyAccount />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/seasonal/:pageId" element={<SeasonalLandingPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/retouren" element={<ReturnsPage />} />
          <Route path="/returns" element={<ReturnsPage />} />
          <Route path="/vendor-register" element={<VendorRegister />} />
          <Route path="/become-vendor" element={<BecomeVendor />} />
          <Route path="/vendor-dashboard" element={<VendorDashboard />} />
          <Route path="/vendor-dashboard/payment-settings" element={<VendorPaymentSettings />} />
          <Route path="/vendor-dashboard/products/new" element={<VendorProductEditor />} />
          <Route path="/vendor-dashboard/products/:productId/edit" element={<VendorProductEditor />} />
          <Route path="/vendor-dashboard/shop-customization" element={<VendorShopCustomization />} />
          <Route path="/vendor-dashboard/shop-templates" element={<VendorShopTemplates />} />
          <Route path="/vendor-dashboard/import" element={<ProductImport />} />
          <Route path="/shop/:vendorId" element={<VendorShop />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="visual-page-editor" element={<VisualPageEditor />} />
            <Route path="visual-design" element={<VisualDesignEditor />} />
            <Route path="design" element={<VisualDesignEditor />} />
            <Route path="design-old" element={<DesignManager />} />
            <Route path="seasonal-themes" element={<SeasonalThemes />} />
            <Route path="seasonal-themes/edit/:themeId" element={<SeasonalThemeEditor />} />
            <Route path="catalogs" element={<CatalogsManager />} />
            <Route path="promotion-settings" element={<PromotionSettings />} />
            <Route path="orders" element={<OrdersManager />} />
            <Route path="customers" element={<Customers />} />
            <Route path="seasonal-landing-pages" element={<SeasonalLandingPages />} />
            <Route path="messages" element={<MessageCenter />} />
            <Route path="homepage-manager" element={<HomepageManager />} />
            <Route path="seasonal-search" element={<SeasonalSearchManager />} />
            <Route path="push-notifications" element={<PushNotifications />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="shipping-settings" element={<ShippingSettings />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="categories" element={<Categories />} />
            <Route path="discount-codes" element={<DiscountCodes />} />
            <Route path="payment-settings" element={<PaymentSettings />} />
            <Route path="partner-sites" element={<PartnerSites />} />
            <Route path="legal-pages" element={<LegalPages />} />
            <Route path="email-settings" element={<EmailSettings />} />
            <Route path="email-templates" element={<EmailTemplatePreview />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="newsletter-settings" element={<NewsletterSettings />} />
            <Route path="newsletter-visual" element={<NewsletterVisualEditor />} />
            <Route path="google-integration" element={<GoogleIntegration />} />
            <Route path="social-media" element={<SocialMediaSettings />} />
            <Route path="document-settings" element={<DocumentSettings />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="brands" element={<BrandsManager />} />
            <Route path="app-icon-settings" element={<AppIconSettings />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="faq" element={<FaqManager />} />
            <Route path="product-image-quality" element={<ProductImageQualityReport />} />
            <Route path="content-creator" element={<ContentCreator />} />
            <Route path="shipping-zones" element={<ShippingZones />} />
            <Route path="blog-themen" element={<BlogTopicsManager />} />
            <Route path="blog-questions" element={<BlogQuestionsManager />} />
            <Route path="section-templates" element={<SectionTemplates />} />
            <Route path="documents" element={<DocumentsManager />} />
            <Route path="vendors" element={<VendorsManager />} />
            <Route path="vendor-settings" element={<VendorSettings />} />
            <Route path="commission-tracking" element={<CommissionTracking />} />
            <Route path="support-center" element={<SupportCenter />} />
            <Route path="admin-management" element={<AdminManagement />} />
            <Route path="profil" element={<AdminProfile />} />
            <Route path="postfach" element={<AdminInbox />} />
            <Route path="content-studio" element={<ContentStudio />} />
            <Route path="catalog-pdf" element={<CatalogPDF />} />
            <Route path="newsletter-builder" element={<NewsletterBuilder />} />
            <Route path="newsletter-designer" element={<NewsletterDesigner />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="products/drafts" element={<ProductDraftsManager />} />
            <Route path="product-drafts" element={<ProductDraftsManager />} />
            <Route path="label-composer" element={<LabelComposer />} />
            <Route path="etiketten" element={<LabelComposer />} />
            <Route path="returns" element={<ReturnsManager />} />
            <Route path="retouren" element={<ReturnsManager />} />
            <Route path="handbuch" element={<AdminManual />} />
            <Route path="feedback" element={<FeedbackManager />} />
            <Route path="campaigns" element={<CampaignManager />} />
            <Route path="benefits" element={<BenefitsManager />} />
            <Route path="vendor-features" element={<VendorFeaturesManager />} />
            <Route path="preview" element={<AdminPreview />} />
            <Route path="marketplace-settings" element={<MarketplaceSettings />} />
            <Route path="maintenance" element={<MaintenanceSettings />} />
            <Route path="launch-checklist" element={<LaunchChecklist />} />
            <Route path="test-email" element={<TestEmail />} />
            <Route path="product-catalog-generator" element={<ProductCatalogGenerator />} />
            <Route path="hair-collections" element={<HairCollectionsManager />} />
            <Route path="collections" element={<HairCollectionsManager />} />
            <Route path="abandoned-carts" element={<AbandonedCartsManager />} />
          </Route>
          <Route path="*" element={
            <div className="App flex flex-col min-h-screen">
              <Navbar />
              <main className="flex-1"><NotFound /></main>
              <Footer />
            </div>
          } />
          </Routes>
          </MaintenanceWrapper>
          {/* Only one chat widget — SupportChatWidget is shown for customers via CustomerOnlyChatWidget.
              FeedbackWidget removed (was causing two stacked chat buttons in bottom-right). */}
          <Toaster position="top-right" richColors />
        </EditorProvider>
      </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
